import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const { lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const texts = {
    nl: { title: "Wachtwoord vergeten", subtitle: "Voer uw e-mailadres in om een resetlink te ontvangen", email: "E-mailadres", submit: "Verstuur resetlink", sent_title: "Link verzonden!", sent_msg: "Als het e-mailadres bij ons bekend is, ontvangt u een resetlink.", back: "Terug naar inloggen" },
    en: { title: "Forgot password", subtitle: "Enter your email address to receive a reset link", email: "Email address", submit: "Send reset link", sent_title: "Link sent!", sent_msg: "If the email address is registered, you will receive a reset link.", back: "Back to login" },
    de: { title: "Passwort vergessen", subtitle: "Geben Sie Ihre E-Mail-Adresse ein, um einen Reset-Link zu erhalten", email: "E-Mail-Adresse", submit: "Reset-Link senden", sent_title: "Link gesendet!", sent_msg: "Wenn die E-Mail-Adresse registriert ist, erhalten Sie einen Reset-Link.", back: "Zur\u00fcck zur Anmeldung" },
    fr: { title: "Mot de passe oubli\u00e9", subtitle: "Entrez votre adresse e-mail pour recevoir un lien de r\u00e9initialisation", email: "Adresse e-mail", submit: "Envoyer le lien", sent_title: "Lien envoy\u00e9!", sent_msg: "Si l'adresse e-mail est enregistr\u00e9e, vous recevrez un lien de r\u00e9initialisation.", back: "Retour \u00e0 la connexion" },
  };
  const t = texts[lang] || texts.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await API.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-[#F9FAFB] px-4" data-testid="forgot-password-sent">
        <div className="w-full max-w-md bg-white border border-border p-8 text-center">
          <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{t.sent_title}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t.sent_msg}</p>
          <Link to="/login"><Button variant="outline" className="rounded-sm" data-testid="back-to-login">{t.back}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center pt-16 bg-[#F9FAFB] px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white border border-border p-8">
        <h1 className="text-2xl font-bold tracking-tight mb-1">{t.title}</h1>
        <p className="text-sm text-muted-foreground mb-6">{t.subtitle}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3" data-testid="forgot-error">{error}</div>}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">{t.email}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-sm" data-testid="forgot-email-input" />
          </div>
          <Button type="submit" className="w-full bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm" disabled={loading} data-testid="forgot-submit-btn">
            {loading ? "..." : t.submit}
          </Button>
        </form>
        <p className="text-sm text-center text-muted-foreground mt-6">
          <Link to="/login" className="text-[#0A3FA3] font-medium hover:underline">{t.back}</Link>
        </p>
      </div>
    </div>
  );
}
